import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const SESSION_KEY = "zt_independence_day_2025_shown";
const POPUP_CONFIG = {
  heading: "Happy Independence Day",
  subheading: "78th Independence Day — Jai Hind",
  tagline: "India's Story Isn't History. It's Compounding.",
  body: "From independence to the world's fastest-growing major economy — build wealth with confidence. Earn up to 20% interest* with Zenstrust's NRI wealth solutions.",
  highlightLabel: "Attractive Returns",
  features: ["Safe & Secure", "Tax Efficient", "Currency Protection", "Global Opportunities"],
  ctaText: "Book a Free Consultation",
  ctaPath: "/#contact",
  disclaimer: "*Interest rates are subject to change. T&C Apply.",
};

// ─── Confetti/Crackers Canvas ─────────────────────────────────────────────────
function ConfettiCanvas({ active }) {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Create particles — tri-color confetti (saffron, white, green) + gold
    const colors = [
      "#FF6700", "#FF9933", "#FFFFFF", "#E8E8E8",
      "#138808", "#1B8C1B", "#DCA520", "#FFD700",
    ];
    const particles = [];
    const PARTICLE_COUNT = 150;
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 4 + Math.random() * 12;
      const size = 4 + Math.random() * 8;
      const shape = Math.random() > 0.5 ? "rect" : "circle";
      particles.push({
        x: cx,
        y: cy,
        vx: Math.cos(angle) * speed * (0.6 + Math.random() * 0.8),
        vy: Math.sin(angle) * speed * (0.5 + Math.random()) - 6,
        size,
        color: colors[Math.floor(Math.random() * colors.length)],
        rotation: Math.random() * 360,
        rotSpeed: (Math.random() - 0.5) * 12,
        shape,
        gravity: 0.15 + Math.random() * 0.1,
        opacity: 1,
        decay: 0.008 + Math.random() * 0.006,
      });
    }
    particlesRef.current = particles;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let alive = 0;
      for (const p of particlesRef.current) {
        if (p.opacity <= 0) continue;
        alive++;
        p.x += p.vx;
        p.y += p.vy;
        p.vy += p.gravity;
        p.vx *= 0.98;
        p.rotation += p.rotSpeed;
        p.opacity -= p.decay;

        ctx.save();
        ctx.globalAlpha = Math.max(0, p.opacity);
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        if (p.shape === "rect") {
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }
      if (alive > 0) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [active]);

  if (!active) return null;
  return (
    <canvas
      ref={canvasRef}
      style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 9996 }}
    />
  );
}

// ─── Smoke Trail Canvas ───────────────────────────────────────────────────────
function SmokeTrailCanvas({ points }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (points.length < 2) return;
    const now = Date.now();
    const FADE = 4500;
    const GAP = 18;
    const stripes = [[255,103,0],[238,238,238],[19,136,8]];
    for (let ci = 0; ci < 3; ci++) {
      const [r,g,b] = stripes[ci];
      const off = (ci - 1) * GAP;
      for (let i = 1; i < points.length; i++) {
        const p0 = points[i-1], p1 = points[i];
        const life = Math.max(0, 1 - (now - p1.t) / FADE);
        if (!life) continue;
        const dx = p1.x-p0.x, dy = p1.y-p0.y;
        const len = Math.hypot(dx,dy)||1;
        const nx = -dy/len, ny = dx/len;
        ctx.beginPath();
        ctx.moveTo(p0.x+nx*off, p0.y+ny*off);
        ctx.lineTo(p1.x+nx*off, p1.y+ny*off);
        ctx.strokeStyle = `rgba(${r},${g},${b},${life*0.82})`;
        ctx.lineWidth = 6 + (1-life)*30;
        ctx.lineCap = "round";
        ctx.stroke();
      }
    }
  });
  return <canvas ref={canvasRef} width={window.innerWidth} height={window.innerHeight}
    style={{position:"fixed",inset:0,pointerEvents:"none",zIndex:9990}} />;
}

// ─── Emirates Airplane SVG ────────────────────────────────────────────────────
// Faces RIGHT. Emirates livery: white fuselage, red cheatline, UAE tail.
function EmiratesPlane() {
  return (
    <svg width="300" height="180" viewBox="0 0 300 180" fill="none" aria-hidden="true"
      style={{filter:"drop-shadow(0 10px 24px rgba(0,0,0,0.38))"}}>
      <defs>
        <linearGradient id="em-fuse" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff"/>
          <stop offset="55%" stopColor="#f0f4f6"/>
          <stop offset="100%" stopColor="#c8d4da"/>
        </linearGradient>
        <linearGradient id="em-wing" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#dce4ea"/>
          <stop offset="100%" stopColor="#a8b8c4"/>
        </linearGradient>
        <linearGradient id="em-eng" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#c0ccd6"/>
          <stop offset="60%" stopColor="#7a8e9c"/>
          <stop offset="100%" stopColor="#5a6e7c"/>
        </linearGradient>
        <linearGradient id="em-tail-bg" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#C8102E"/>
          <stop offset="100%" stopColor="#E8192E"/>
        </linearGradient>
        <linearGradient id="em-win" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#d0eaf8"/>
          <stop offset="100%" stopColor="#7ab8d8"/>
        </linearGradient>
      </defs>

      {/* ── WINGS ── */}
      <path d="M186 88 L142 32 Q134 22 122 22 L106 24 L162 88 Z" fill="url(#em-wing)" stroke="#98aab4" strokeWidth="0.8"/>
      <path d="M186 94 L142 150 Q134 160 122 160 L106 158 L162 94 Z" fill="url(#em-wing)" stroke="#98aab4" strokeWidth="0.8"/>
      {/* flaps */}
      <path d="M172 88 L136 36 L126 38 L156 88 Z" fill="#b0bec8" opacity="0.7"/>
      <path d="M172 94 L136 146 L126 144 L156 94 Z" fill="#b0bec8" opacity="0.7"/>
      {/* winglets */}
      <path d="M106 24 Q100 18 102 10 L109 13 L108 24 Z" fill="#a0b0bc" stroke="#8898a4" strokeWidth="0.6"/>
      <path d="M106 158 Q100 164 102 172 L109 169 L108 158 Z" fill="#a0b0bc" stroke="#8898a4" strokeWidth="0.6"/>

      {/* ── ENGINES ── */}
      <ellipse cx="154" cy="64" rx="20" ry="10" fill="url(#em-eng)" stroke="#6a7a88" strokeWidth="0.8"/>
      <ellipse cx="138" cy="64" rx="8" ry="10" fill="#4a5e6e"/>
      <ellipse cx="138" cy="64" rx="5" ry="7" fill="#2e424e"/>
      <ellipse cx="173" cy="64" rx="6" ry="8" fill="#7a8e9c"/>
      <path d="M154 74 L156 88 L161 88 L159 74 Z" fill="#98acb8"/>

      <ellipse cx="154" cy="118" rx="20" ry="10" fill="url(#em-eng)" stroke="#6a7a88" strokeWidth="0.8"/>
      <ellipse cx="138" cy="118" rx="8" ry="10" fill="#4a5e6e"/>
      <ellipse cx="138" cy="118" rx="5" ry="7" fill="#2e424e"/>
      <ellipse cx="173" cy="118" rx="6" ry="8" fill="#7a8e9c"/>
      <path d="M154 108 L156 94 L161 94 L159 108 Z" fill="#98acb8"/>

      {/* ── FUSELAGE ── */}
      <path d="M34 86 Q22 86 10 90 Q4 91 4 91 Q4 91 10 93 Q22 97 34 97 L178 97 Q210 97 248 93 Q276 91 284 91 Q276 91 248 89 Q210 86 178 86 Z"
        fill="url(#em-fuse)" stroke="#c8d4dc" strokeWidth="0.7"/>

      {/* Emirates red cheatline */}
      <path d="M34 88 Q120 86.5 178 86 Q220 85.5 268 88"
        stroke="#C8102E" strokeWidth="3.5" fill="none" opacity="0.92"/>

      {/* Nose */}
      <path d="M248 88 Q272 89 284 91 Q272 93 248 94 Z" fill="#e4eaee" stroke="#c0ccd4" strokeWidth="0.6"/>
      <path d="M274 90.5 Q280 91 284 91 Q280 91.5 274 91.5 Z" fill="#5a6a74"/>

      {/* Cockpit windows */}
      <path d="M250 88 Q260 86.5 268 87.5 L270 89.5 Q262 88.5 252 89.5 Z" fill="url(#em-win)" opacity="0.95"/>
      <path d="M250 94 Q260 95.5 268 94.5 L270 92.5 Q262 93.5 252 92.5 Z" fill="url(#em-win)" opacity="0.95"/>
      <rect x="268" y="88.5" width="5" height="5" rx="1" fill="#4890b8" opacity="0.9"/>

      {/* Passenger windows */}
      {[68,80,92,104,116,128,140,152,164,176,190,204,218,232].map(x=>(
        <rect key={x} x={x} y="87.5" width="8" height="6" rx="2.5" fill="url(#em-win)" stroke="#68a8cc" strokeWidth="0.3" opacity="0.88"/>
      ))}
      {/* Door outlines */}
      <rect x="58" y="86" width="7" height="11" rx="2" fill="none" stroke="#a0b4bc" strokeWidth="0.7" opacity="0.6"/>
      <rect x="238" y="86" width="7" height="11" rx="2" fill="none" stroke="#a0b4bc" strokeWidth="0.7" opacity="0.6"/>

      {/* ── VERTICAL TAIL FIN (Emirates red) ── */}
      <path d="M40 86 Q36 72 30 50 Q38 47 46 58 L48 86 Z" fill="url(#em-tail-bg)" stroke="#a00820" strokeWidth="0.6"/>
      {/* UAE gold calligraphy hint on tail */}
      <text x="34" y="74" fontFamily="serif" fontSize="9" fill="gold" opacity="0.7" transform="rotate(-15,34,74)">إمارات</text>

      {/* ── HORIZONTAL STABILIZERS ── */}
      <path d="M48 90 L22 74 Q18 71 16 72 L14 76 L42 90 Z" fill="url(#em-wing)" stroke="#98aab4" strokeWidth="0.7"/>
      <path d="M48 92 L22 108 Q18 111 16 110 L14 106 L42 92 Z" fill="url(#em-wing)" stroke="#98aab4" strokeWidth="0.7"/>

      {/* ── TAIL CONE / APU ── */}
      <ellipse cx="30" cy="91" rx="5" ry="8" fill="#98aab4" stroke="#7a8e98" strokeWidth="0.5"/>

      {/* Landing gear fairings */}
      <ellipse cx="124" cy="98" rx="13" ry="4" fill="#8898a4" opacity="0.45"/>
      <ellipse cx="188" cy="98" rx="10" ry="3" fill="#8898a4" opacity="0.38"/>

      {/* Belly shading */}
      <path d="M80 97 Q150 101 210 97 Q210 103 150 105 Q90 103 80 97 Z" fill="#8898a8" opacity="0.28"/>

      {/* Cable attach */}
      <circle cx="8" cy="91" r="5" fill="#6a7a84" stroke="#4a5a64" strokeWidth="1.2"/>
      <circle cx="8" cy="91" r="2.2" fill="#3a4a54"/>
    </svg>
  );
}

// ─── Image Banner ────────────────────────────────────────────────────────────
function WavingBanner({ waveTime, flat = false, width = 780, height = 468 }) {
  // Simulate a waving cloth effect using CSS perspective skew on the img
  const wave = flat ? 0 : Math.sin(waveTime * Math.PI * 2) * 6;
  const wave2 = flat ? 0 : Math.sin(waveTime * Math.PI * 2 + 1.4) * 4;
  return (
    <div
      aria-hidden="true"
      style={{
        width,
        height,
        filter: "drop-shadow(4px 8px 18px rgba(0,0,0,0.48))",
        borderRadius: 10,
        overflow: "hidden",
        // left edge is towed (stable), right edge waves via perspective skew
        transform: `perspective(600px) rotateX(${wave2 * 0.4}deg) rotateY(${wave * 0.5}deg)`,
        transformOrigin: "left center",
      }}
    >
      <img
        src="/Zestruest indipendance.png"
        alt="Zenstrust Independence Day — Earn up to 20% interest"
        draggable={false}
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", userSelect: "none" }}
      />
    </div>
  );
}

// ─── Main orchestrator ────────────────────────────────────────────────────────
// Phases: idle → flying → expanding → expanded → done
export default function IndependenceDayBanner({ config = POPUP_CONFIG }) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("idle");
  const [planePos, setPlanePos] = useState({ x: -300, y: 0 });
  const [waveTime, setWaveTime] = useState(0);
  // banner expand: 0 = tiny towed, 1 = full size centered
  const [expandT, setExpandT] = useState(0);
  // position where banner was when plane exited
  const detachPos = useRef({ x: 0, y: 0 });

  const rafRef = useRef(null);
  const waveRafRef = useRef(null);
  const expandRafRef = useRef(null);
  const startRef = useRef(null);
  const waveStartRef = useRef(null);
  const smokeRef = useRef([]);
  const [smokePoints, setSmokePoints] = useState([]);

  const FLY_DURATION = 5000;
  const EXPAND_DURATION = 900;
  const CONFETTI_DURATION = 1200;
  const ANGLE = -36;
  // Responsive banner dimensions - max 90% of viewport, maintain aspect ratio
  const maxWidth = Math.min(780, window.innerWidth * 0.9);
  const maxHeight = Math.min(468, window.innerHeight * 0.85);
  const aspectRatio = 780 / 468;
  let BANNER_W, BANNER_H;
  if (maxWidth / aspectRatio <= maxHeight) {
    BANNER_W = maxWidth;
    BANNER_H = maxWidth / aspectRatio;
  } else {
    BANNER_H = maxHeight;
    BANNER_W = maxHeight * aspectRatio;
  }

  // wave loop
  const startWave = useCallback(() => {
    waveStartRef.current = null;
    const loop = (ts) => {
      if (!waveStartRef.current) waveStartRef.current = ts;
      setWaveTime(((ts - waveStartRef.current) % 2200) / 2200);
      waveRafRef.current = requestAnimationFrame(loop);
    };
    waveRafRef.current = requestAnimationFrame(loop);
  }, []);

  const addSmoke = useCallback((x, y) => {
    smokeRef.current.push({ x, y, t: Date.now() });
    if (smokeRef.current.length > 260) smokeRef.current.shift();
    setSmokePoints([...smokeRef.current]);
  }, []);

  // compute banner position while towed
  const getBannerPos = useCallback((px, py) => {
    const rad = ANGLE * Math.PI / 180;
    const tailX = px - 140;
    const tailY = py + 2;
    const cable = 90;
    const bRX = tailX - Math.cos(rad) * cable;
    const bRY = tailY - Math.sin(rad) * cable;
    return { x: bRX - BANNER_W, y: bRY - BANNER_H / 2 };
  }, []);

  const fly = useCallback(() => {
    setPhase("flying");
    startRef.current = null;
    const sX = -320, sY = window.innerHeight + 140;
    const eX = window.innerWidth + 320, eY = -140;
    const ease = (t) => t < 0.12 ? t * t / 0.12 * 0.5 : 0.5 * 0.12 + (t - 0.12) * (1 / (1 - 0.12)) * (1 - 0.5 * 0.12);

    let detached = false;

    const tick = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const raw = Math.min((ts - startRef.current) / FLY_DURATION, 1);
      const t = Math.min(ease(raw), 1);
      const x = sX + (eX - sX) * t;
      const y = sY + (eY - sY) * t;
      setPlanePos({ x, y });
      addSmoke(x - 40, y + 12);

      // detach at mid-screen (raw ≈ 0.5)
      if (!detached && raw >= 0.5) {
        detached = true;
        const bp = getBannerPos(x, y);
        detachPos.current = bp;
        // stop wave, start confetti burst
        if (waveRafRef.current) cancelAnimationFrame(waveRafRef.current);
        setPhase("confetti"); // plane still flies, confetti bursts

        // After confetti, start banner expand
        setTimeout(() => {
          setPhase("expanding");
          let expandStart = null;
          const expandTick = (ets) => {
            if (!expandStart) expandStart = ets;
            const et = Math.min((ets - expandStart) / EXPAND_DURATION, 1);
            const spring = et < 0.5 ? 4*et*et*et : 1 - Math.pow(-2*et+2,3)/2;
            setExpandT(spring);
            if (et < 1) {
              expandRafRef.current = requestAnimationFrame(expandTick);
            } else {
              setExpandT(1);
              setPhase("expanded");
            }
          };
          expandRafRef.current = requestAnimationFrame(expandTick);
        }, CONFETTI_DURATION);
      }

      if (raw < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
      // plane just exits — phase already set to detached-flying or expanded
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [addSmoke, getBannerPos]);

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY)) return;
    startWave();
    const t = setTimeout(fly, 350);
    return () => clearTimeout(t);
  }, [fly, startWave]);

  useEffect(() => () => {
    [rafRef, waveRafRef, expandRafRef].forEach(r => r.current && cancelAnimationFrame(r.current));
  }, []);

  const handleClose = useCallback(() => {
    sessionStorage.setItem(SESSION_KEY, "1");
    setPhase("done");
  }, []);

  if (phase === "idle" || phase === "done") return null;

  // target: banner expands in place — same position as detach, but scaled up
  // no movement, just scale from small to big, centered on detach point
  const detachCX = detachPos.current.x + BANNER_W / 2;
  const detachCY = detachPos.current.y + BANNER_H / 2;
  // scaled banner: interpolate from detach center → viewport center
  const scaledW = BANNER_W * (0.45 + 0.55 * expandT);
  const scaledH = BANNER_H * (0.45 + 0.55 * expandT);
  const vpCX = window.innerWidth / 2;
  const vpCY = window.innerHeight / 2;
  const centerX = detachCX + (vpCX - detachCX) * expandT;
  const centerY = detachCY + (vpCY - detachCY) * expandT;
  const exBannerX = centerX - scaledW / 2;
  const exBannerY = centerY - scaledH / 2;
  const exAngle = ANGLE * (1 - expandT);
  const exScale = 0.45 + 0.55 * expandT;

  // during flight: towed position
  const bp = getBannerPos(planePos.x, planePos.y);
  const tailX = planePos.x - 140;
  const tailY = planePos.y + 2;
  const bRX = bp.x + BANNER_W;

  const isFlying = phase === "flying";
  const planeVisible = phase === "flying" || phase === "confetti" || phase === "expanding";
  const bannerExpanding = phase === "expanding" || phase === "expanded";
  const confettiActive = phase === "confetti" || phase === "expanding";

  return (
    <>
      <style>{`
        @keyframes zt-banner-bob {
          0%,100% { transform: translateY(0); }
          50%      { transform: translateY(-8px); }
        }
        @keyframes zt-fade-bg {
          from { background: rgba(10,30,35,0); }
          to   { background: rgba(10,30,35,0.55); }
        }
      `}</style>

      {/* Smoke */}
      {(phase === "flying" || phase === "confetti" || phase === "expanding") && (
        <SmokeTrailCanvas points={smokePoints} />
      )}

      {/* Confetti/Crackers burst */}
      <ConfettiCanvas active={confettiActive} />

      {/* ── Towed banner (only while plane is flying and hasn't detached yet) ── */}
      {isFlying && (
        <>
          <svg aria-hidden="true" style={{position:"fixed",inset:0,width:"100vw",height:"100vh",pointerEvents:"none",zIndex:9993,overflow:"visible"}}>
            <line x1={tailX} y1={tailY-12} x2={bRX} y2={bp.y+28} stroke="#bbb" strokeWidth="1.6" strokeDasharray="5 3" opacity="0.72"/>
            <line x1={tailX} y1={tailY+12} x2={bRX} y2={bp.y+BANNER_H-28} stroke="#bbb" strokeWidth="1.6" strokeDasharray="5 3" opacity="0.72"/>
          </svg>
          <div aria-hidden="true" style={{
            position:"fixed", left:bp.x, top:bp.y,
            zIndex:9994, pointerEvents:"none",
            transform:`rotate(${ANGLE}deg)`,
            transformOrigin:"right center",
          }}>
            <WavingBanner waveTime={waveTime} width={BANNER_W} height={BANNER_H}/>
          </div>
        </>
      )}

      {/* ── Plane (visible until it exits screen) ── */}
      {planeVisible && (
        <div aria-hidden="true" style={{
          position:"fixed",
          left:planePos.x-150, top:planePos.y-90,
          zIndex:9995, pointerEvents:"none",
          transform:`rotate(${ANGLE}deg)`,
          willChange:"left,top",
        }}>
          <EmiratesPlane/>
        </div>
      )}

      {/* ── Banner expanding / expanded in place ── */}
      {bannerExpanding && (
        <>
          {/* Backdrop */}
          <div
            style={{
              position:"fixed", inset:0, zIndex:9996,
              background:`rgba(10,30,35,${0.58 * expandT})`,
              backdropFilter:`blur(${expandT*5}px)`,
              pointerEvents: phase === "expanded" ? "auto" : "none",
            }}
            onClick={(e) => { if (e.target === e.currentTarget && phase === "expanded") handleClose(); }}
            onKeyDown={(e) => { if (e.key === "Escape") handleClose(); }}
            tabIndex={phase === "expanded" ? 0 : -1}
          />

          {/* Banner — grows in place, straightens, stops waving */}
          <div
            role={phase === "expanded" ? "dialog" : undefined}
            aria-modal={phase === "expanded" ? "true" : undefined}
            aria-labelledby="id-banner-heading"
            style={{
              position:"fixed",
              left: exBannerX,
              top: exBannerY,
              zIndex:9997,
              pointerEvents: phase === "expanded" ? "auto" : "none",
              transform:`rotate(${exAngle}deg) scale(${exScale})`,
              transformOrigin:"center center",
            }}
          >
            <WavingBanner waveTime={waveTime} flat={phase === "expanded"} width={BANNER_W} height={BANNER_H}/>
          </div>

          {/* Close — only when fully expanded */}
          {phase === "expanded" && (
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close banner"
              style={{
                position:"fixed",
                left: exBannerX + scaledW - 18,
                top: exBannerY - 18,
                zIndex:9998,
                width:38, height:38, borderRadius:"50%",
                background:"rgba(19,57,62,0.92)", border:"none",
                cursor:"pointer", display:"grid", placeItems:"center",
                color:"white", boxShadow:"0 4px 18px rgba(0,0,0,0.45)",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
              </svg>
            </button>
          )}

          {/* CTA overlay */}
          {phase === "expanded" && (
            <button
              type="button"
              onClick={() => { handleClose(); navigate(config.ctaPath); }}
              aria-label="Register at Zenstrust"
              style={{
                position:"fixed",
                left: exBannerX + 18 * exScale,
                top: exBannerY + (scaledH * 0.82),
                width: scaledW * 0.46, height: scaledH * 0.11,
                background:"transparent", border:"none", cursor:"pointer", borderRadius:8,
                zIndex:9998,
              }}
            />
          )}
        </>
      )}
    </>
  );
}
